import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import { useTranslation } from 'react-i18next';
import { View, Text, StyleSheet } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import {
  getProductFieldDisplayLabel,
  isReqProductFieldVisibility,
} from "../../../../../helper/sendValidationHelper";
import { matchesValidationRegEx } from "../../../../../helper/fieldHelper";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import FloatingDropdownComponent from "../../../common/FloatingDropdownComponent";
import PersonIcon from "../../../../../../../../assets/images/user-round.svg";

interface AdditionalInformationStepProps {
  onValidationChange?: (valid: boolean) => void;
}

export interface AdditionalInformationStepHandle {
  submit: () => boolean;
}

/**
 * Input filter types for additional-info XmlTag fields.
 * Add a new type here and map it in ADDITIONAL_FIELD_INPUT_FILTER_REGEX.
 */
type AdditionalFieldInputFilterType = "numeric";

/**
 * XmlTags that should constrain typed input via regex.
 * To apply a filter to another field, add `{ xmlTag, type }` — no other code changes needed.
 */
const ADDITIONAL_FIELD_INPUT_FILTERS: ReadonlyArray<{
  xmlTag: string;
  type: AdditionalFieldInputFilterType;
}> = [
  { xmlTag: "receiverPhone", type: "numeric" },
];

/** Regex used to strip disallowed characters for each filter type (global, replace with ""). */
const ADDITIONAL_FIELD_INPUT_FILTER_REGEX: Record<AdditionalFieldInputFilterType, RegExp> = {
  numeric: /\D/g, // keep digits 0-9 only
};

function applyAdditionalFieldInputFilter(xmlTag: string, text: string): string {
  const rule = ADDITIONAL_FIELD_INPUT_FILTERS.find(
    (r) => r.xmlTag.toLowerCase() === xmlTag.toLowerCase(),
  );
  if (!rule) return text;
  return text.replace(ADDITIONAL_FIELD_INPUT_FILTER_REGEX[rule.type], "");
}

function getAdditionalFieldInputFilterType(
  xmlTag: string,
): AdditionalFieldInputFilterType | undefined {
  return ADDITIONAL_FIELD_INPUT_FILTERS.find(
    (r) => r.xmlTag.toLowerCase() === xmlTag.toLowerCase(),
  )?.type;
}

function buildSendAdditionalFieldErrors(
  requiredFields: any[],
  additionalFieldValues: Record<string, any> | null | undefined,
): Record<string, string> {
  const errs: Record<string, string> = {};
  for (const f of requiredFields) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const label = getProductFieldDisplayLabel(f, key);
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const value = String(additionalFieldValues?.[key] ?? "");
    if (!value) errs[key] = `${label} is required`;
    else if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
    else if (value.length > max) errs[key] = `${label} must be at most ${max} characters`;
    else if (!matchesValidationRegEx(f, value)) errs[key] = `${label} contains invalid characters`;
  }
  return errs;
}

function isOccupationField(label: string): boolean {
  return (label ?? "").toLowerCase().includes("occupation");
}

/** Unwrap cc:enumeratedValueInfo / nested shapes from getFieldsForProduct. */
function parseEnumeratedRow(opt: any): { value: string; label: string } | null {
  const row = opt?.enumeratedValueInfo ?? opt?.EnumeratedValueInfo ?? opt;
  if (!row || typeof row !== "object") return null;
  const value = String(row?.Value ?? row?.value ?? "").trim();
  if (!value) return null;
  const labelRaw = row?.Label ?? row?.label;
  const label =
    labelRaw != null && String(labelRaw).trim() !== ""
      ? String(labelRaw).trim()
      : value;
  return { value, label };
}

/** Options for dropdown: label shown to user, value sent on send validation (additionalFieldValues / FieldValues). */
function getEnumeratedOptions(field: any): Array<{ value: string; label: string }> {
  let raw = field?.enumeratedValues ?? field?.EnumeratedValues;
  if (raw == null) return [];
  if (!Array.isArray(raw)) raw = [raw];
  const out: Array<{ value: string; label: string }> = [];
  for (const item of raw) {
    const parsed = parseEnumeratedRow(item);
    if (parsed) out.push(parsed);
  }
  return out;
}

/** Shared with MainSendCashScreen handleNext so Continue uses live store values, not stale isStepValid. */
export function areSendAdditionalFieldsValid(
  missingRequiredFields: any[] | null | undefined,
  additionalFieldValues: Record<string, any> | null | undefined,
): boolean {
  const requiredFields = (missingRequiredFields || []).filter((f: any) =>
    isReqProductFieldVisibility(f?.visibility ?? f?.Visibility),
  );
  for (const f of requiredFields) {
    const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
    const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
    const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
    const value = String(additionalFieldValues?.[key] ?? "");
    if (value.length < min || value.length > max || !value) return false;
    if (!matchesValidationRegEx(f, value)) return false;
  }
  return true;
}

const AdditionalInformationStep = forwardRef<
  AdditionalInformationStepHandle,
  AdditionalInformationStepProps
>(({ onValidationChange }, ref) => {
  const { t } = useTranslation();
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  const requiredFields = useMemo(
    () =>
      (missingRequiredFields || []).filter((f: any) =>
        isReqProductFieldVisibility(f?.visibility ?? f?.Visibility),
      ),
    [missingRequiredFields],
  );

  const computeFieldErrors = useCallback(
    () => buildSendAdditionalFieldErrors(requiredFields, additionalFieldValues),
    [requiredFields, additionalFieldValues],
  );

  useEffect(() => {
    onValidationChange?.(
      areSendAdditionalFieldsValid(missingRequiredFields, additionalFieldValues),
    );
  }, [requiredFields, additionalFieldValues, missingRequiredFields, onValidationChange]);

  useEffect(() => {
    if (!showFieldErrors) return;
    setFieldErrors(computeFieldErrors());
  }, [showFieldErrors, computeFieldErrors]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      const errs = computeFieldErrors();
      setFieldErrors(errs);
      setShowFieldErrors(true);
      return areSendAdditionalFieldsValid(missingRequiredFields, additionalFieldValues);
    },
  }));

  const handleChange = (key: string, text: string) => {
    setAdditionalFieldValue?.(key, applyAdditionalFieldInputFilter(key, text));
  };

  // if (!requiredFields?.length) {
  //   return (
  //     <View style={styles.container}>
  //       <Text>No additional information required.</Text>
  //     </View>
  //   );
  // }

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { paddingRight: 100 }]}>{t("WE_NEED_FEW_MORE_DETAILS")}</Text>
      {requiredFields.map((f: any, index: number) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = getProductFieldDisplayLabel(f, key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = showFieldErrors ? fieldErrors[key] : undefined;
        const options = getEnumeratedOptions(f);
        const useEnumeratedDropdown = options.length > 0;
        // DEBUG only — do not commit. Comment/uncomment the block below to toggle.
        // const debugValidationRegEx = String(f?.ValidationRegEx ?? f?.validationRegEx ?? "(none)");

        return (
          <View key={key} style={styles.fieldWrapper}>
            {useEnumeratedDropdown ? (
              <FloatingDropdownComponent
                testID={`sendAdditionalField_${key}_dropdown`}
                optionTestID={`sendAdditionalField_${key}_option`}
                label={label}
                value={value}
                displayValue={options.find((o) => o.value === value)?.label}
                options={options}
                onSelect={(selected) => setAdditionalFieldValue?.(key, selected)}
                isOpen={openDropdownKey === key}
                onOpenChange={(open) => setOpenDropdownKey(open ? key : null)}
                errorText={error}
              />
            ) : (
              <>
                <FloatingTextInputComponent
                  testID={`sendAdditionalField_${key}`}
                  label={placeholder}
                  value={value}
                  onChangeText={(t) => handleChange(key, t)}
                  isAutoFocus={false}
                  keyboardType={
                    getAdditionalFieldInputFilterType(key) === "numeric" ? "number-pad" : undefined
                  }
                />
                {!!error && <Text style={styles.error}>{error}</Text>}
              </>
            )}
            {/* ===== DEBUG: XmlTag + ValidationRegEx (comment out when not needed; do not commit) ===== */}
            {/* <Text style={styles.debugMeta}>
              {`xmlTag: ${key}\nregex: ${debugValidationRegEx}`}
            </Text> */}
            {/* ===== /DEBUG ===== */}
          </View>
        );
      })}
      {requiredFields.some((f: any) => isOccupationField(String(f?.FieldLabel ?? f?.fieldLabel ?? ""))) && (
        <View style={styles.infoBox}>
          <View style={styles.infoBoxIconWrap}>
            <PersonIcon width={24} height={24} />
          </View>
          <View style={styles.infoBoxContent}>
            <Text style={styles.infoBoxTitle}>Why do we ask these questions?</Text>
            <Text style={styles.infoBoxBody}>
              Regulations require us to verify identity before you send money. This helps prevent identity theft and keep information safe.
            </Text>
          </View>
        </View>
      )}
    </View>
  );
});

AdditionalInformationStep.displayName = "AdditionalInformationStep";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  fieldWrapper: {
    marginBottom: 16
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#1F1F1F",
    borderRadius: 8,
  },
  infoBoxIconWrap: {
    paddingRight: 16,
    paddingLeft: 4
  },
  infoBoxContent: {
    flex: 1,
  },
  infoBoxTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111",
    marginBottom: 4,
  },
  infoBoxBody: {
    fontSize: 14,
    fontWeight: "400",
    color: "#374151",
    lineHeight: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    marginTop: 8,
    marginBottom: 16,
    color: "#111"
  },
  label: {
    fontSize: 14,
    color: "#111",
    marginBottom: 6
  },
  error: {
    color: "#E11D48",
    marginTop: 6,
    fontSize: 12
  },
  // DEBUG only — do not commit
  debugMeta: {
    marginTop: 4,
    fontSize: 11,
    fontFamily: "monospace",
    color: "#6B7280",
  },
});

export default AdditionalInformationStep;
